import { collection, documentId, query, where, orderBy, limit, startAfter, endBefore, getDocs } from "firebase/firestore";
import { Post } from "@/types/post";
import { mapDocToPost } from "@/utils/mapDocToPost.web";
import { readFeedCache, writeFeedCache } from "@/utils/feedCache.web";
import { cachePosts } from "@/utils/postCache";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import * as firebaseWeb from "@/firebase/web"; // Import firebase/web as a module

const firestore = firebaseWeb.firestore; // Use the exported firestore instance
type DocumentSnapshot = firebaseWeb.DocumentSnapshot;


type PostsContextType = {
  posts: Post[];
  setPosts: React.Dispatch<React.SetStateAction<Post[]>>;
  addPost: (post: Post) => void;
  /** True only during the very first fetch when no posts are available yet. The only flag UI should use to gate a full-screen spinner. */
  initialLoading: boolean;
  /** True while the feed is silently fetching newer posts in the background. UI should never block on this; surface it as a subtle indicator. */
  backgroundRefreshing: boolean;
  loadingMore: boolean;
  /** True during an explicit user-triggered pull-to-refresh. */
  refreshing: boolean;
  hasMore: boolean;
  /** Epoch ms of the last successful fetch (initial or refresh). 0 until the first fetch completes. */
  lastFetchedAt: number;
  fetchInitialPosts: () => Promise<void>;
  fetchMorePosts: () => Promise<void>;
  onRefresh: () => Promise<void>;
  /** Quietly fetch newer posts if the last fetch is older than `maxAgeMs`. No-op otherwise. */
  refreshIfStale: (maxAgeMs?: number) => Promise<void>;
  /** Re-fetch denormalized `likes` / `commentCount` for the top N posts in state and patch them in place. Used on focus regain since per-card listeners were removed in D1. */
  refreshVisibleCounts: (limit?: number) => Promise<void>;
};

const PostsContext = createContext<PostsContextType | undefined>(undefined);

export const usePosts = () => {
  const context = useContext(PostsContext);
  if (!context) {
    throw new Error("usePosts must be used within a PostsProvider");
  }
  return context;
};

const POSTS_PER_PAGE = 10;
const MAX_POSTS_IN_MEMORY = 50;
const DEFAULT_STALE_MS = 60_000;

export const PostsProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading: isAuthLoading } = useAuth();
  // Hydrate the feed synchronously from sessionStorage so the first paint
  // already has posts. The fetch effect below will then silently bring in
  // newer posts (backgroundRefreshing flips, initialLoading stays false).
  const cachedInitialPosts = useMemo(() => readFeedCache(), []);
  const [posts, setPosts] = useState<Post[]>(cachedInitialPosts);
  const [initialLoading, setInitialLoading] = useState(
    cachedInitialPosts.length === 0
  );
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [firstVisible, setFirstVisible] = useState<DocumentSnapshot | null>(
    null
  );
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState(0);

  // Refs so callbacks below can read live values without rebinding on every change.
  const postsRef = useRef<Post[]>(cachedInitialPosts);
  const postsLengthRef = useRef(0);
  const lastFetchedAtRef = useRef(0);
  const firstVisibleRef = useRef<DocumentSnapshot | null>(null);
  const backgroundRefreshingRef = useRef(false);
  const refreshingRef = useRef(false);
  // Guard so concurrent focus + visibilitychange events don't fire two queries.
  const visibleCountsInFlightRef = useRef(false);
  // Tracks which uid we've already kicked off an initial fetch for; prevents
  // re-fetching when the auth state flickers (token refresh) for the same user.
  const fetchedForUidRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    postsRef.current = posts;
    postsLengthRef.current = posts.length;
  }, [posts]);
  useEffect(() => {
    lastFetchedAtRef.current = lastFetchedAt;
  }, [lastFetchedAt]);
  useEffect(() => {
    firstVisibleRef.current = firstVisible;
  }, [firstVisible]);
  useEffect(() => {
    backgroundRefreshingRef.current = backgroundRefreshing;
  }, [backgroundRefreshing]);
  useEffect(() => {
    refreshingRef.current = refreshing;
  }, [refreshing]);

  // Persist whatever's currently on screen so a reload or new tab gets to
  // skip the cold-start spinner. Guard against the empty-array transition
  // (logout, pre-first-fetch) so we don't clobber a useful cache with nothing.
  useEffect(() => {
    if (posts.length === 0) return;
    writeFeedCache(posts);
    // Keep the in-memory by-id cache in sync so PostDetailsScreen can paint
    // instantly for anything the user has seen in the feed.
    cachePosts(posts);
  }, [posts]);

  const addPost = (post: Post) => {
    setPosts((prevPosts) => {
      if (prevPosts.some((p) => p.id === post.id)) return prevPosts;
      return [post, ...prevPosts];
    });
  };

  const fetchInitialPosts = useCallback(async () => {
    const isCold = postsLengthRef.current === 0;
    if (isCold) {
      setInitialLoading(true);
    } else {
      setBackgroundRefreshing(true);
    }
    try {
      const postsCollection = collection(firestore, "posts");
      const q = query(
        postsCollection,
        where("plateFollowersOnly", "==", false),
        where("groupId", "==", null),
        orderBy("createdAt", "desc"),
        limit(POSTS_PER_PAGE)
      );
      const querySnapshot = await getDocs(q);

      const postsData: Post[] = querySnapshot.docs.map(mapDocToPost);
      setPosts(postsData);

      if (querySnapshot.docs.length > 0) {
        setFirstVisible(querySnapshot.docs[0]);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length === POSTS_PER_PAGE);
      } else {
        setHasMore(false);
      }
      setLastFetchedAt(Date.now());
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setInitialLoading(false);
      setBackgroundRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthLoading) return;
    const currentUid = user?.uid ?? null;
    if (fetchedForUidRef.current === currentUid) return;
    fetchedForUidRef.current = currentUid;
    fetchInitialPosts();
  }, [user?.uid, isAuthLoading, fetchInitialPosts]);

  const fetchMorePosts = async () => {
    if (loadingMore || !hasMore || !lastVisible) return;

    setLoadingMore(true);
    try {
      const postsCollection = collection(firestore, "posts");
      const q = query(
        postsCollection,
        where("plateFollowersOnly", "==", false),
        where("groupId", "==", null),
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(POSTS_PER_PAGE)
      );
      const querySnapshot = await getDocs(q);
      const newPostsData = querySnapshot.docs.map(mapDocToPost);

      if (newPostsData.length > 0) {
        setPosts((prevPosts) => {
          const allPosts = [...prevPosts, ...newPostsData];
          const uniquePosts = Array.from(
            new Map(allPosts.map((p) => [p.id, p])).values()
          );
          return uniquePosts.slice(-MAX_POSTS_IN_MEMORY);
        });
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        setHasMore(querySnapshot.docs.length === POSTS_PER_PAGE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching more posts:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Shared "fetch newer posts" logic used by both the explicit pull-to-refresh
  // and the silent background refresh. Mode controls which UI flag mutates.
  const refreshNewer = useCallback(async (mode: "foreground" | "background") => {
    if (mode === "foreground") {
      if (refreshingRef.current) return;
      setRefreshing(true);
    } else {
      if (backgroundRefreshingRef.current) return;
      setBackgroundRefreshing(true);
    }
    try {
      const postsCollection = collection(firestore, "posts");
      let q = query(
        postsCollection,
        where("plateFollowersOnly", "==", false),
        where("groupId", "==", null),
        orderBy("createdAt", "desc"),
        limit(POSTS_PER_PAGE)
      );

      const currentFirstVisible = firstVisibleRef.current;
      if (currentFirstVisible) {
        q = query(q, endBefore(currentFirstVisible));
      }

      const querySnapshot = await getDocs(q);
      const newPostsData = querySnapshot.docs.map(mapDocToPost);

      if (newPostsData.length > 0) {
        if (currentFirstVisible) {
          setPosts((prevPosts) => {
            const allPosts = [...newPostsData, ...prevPosts];
            const uniquePosts = Array.from(
              new Map(allPosts.map((p) => [p.id, p])).values()
            );
            return uniquePosts.slice(0, MAX_POSTS_IN_MEMORY);
          });
        } else {
          setPosts(newPostsData);
          setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
          setHasMore(querySnapshot.docs.length === POSTS_PER_PAGE);
        }
        setFirstVisible(querySnapshot.docs[0]);
      }
      setLastFetchedAt(Date.now());
    } catch (error) {
      console.error("Error refreshing posts:", error);
    } finally {
      if (mode === "foreground") {
        setRefreshing(false);
      } else {
        setBackgroundRefreshing(false);
      }
    }
  }, []);

  const onRefresh = useCallback(() => refreshNewer("foreground"), [refreshNewer]);

  const refreshIfStale = useCallback(
    async (maxAgeMs: number = DEFAULT_STALE_MS) => {
      if (lastFetchedAtRef.current === 0) return;
      if (Date.now() - lastFetchedAtRef.current < maxAgeMs) return;
      await refreshNewer("background");
    },
    [refreshNewer]
  );

  // Top-N count refresh — pairs with D1 (dropping per-card listeners). Without
  // this, an on-screen post's likes/commentCount stay frozen until a wholesale
  // refetch, because refreshNewer's endBefore(firstVisible) by design excludes
  // anything already visible. One batched getDocs is bounded (max 30 by
  // Firestore's `in` cap, we ask for 10) and only triggers cards whose counts
  // actually changed via Map-based reference preservation below.
  const refreshVisibleCounts = useCallback(async (top: number = 10) => {
    if (visibleCountsInFlightRef.current) return;
    const ids = postsRef.current.slice(0, top).map((p) => p.id);
    if (ids.length === 0) return;
    visibleCountsInFlightRef.current = true;
    try {
      const q = query(
        collection(firestore, "posts"),
        where(documentId(), "in", ids)
      );
      const snap = await getDocs(q);
      if (snap.docs.length === 0) return;
      const updates = new Map<string, { likes: number; commentCount: number }>();
      snap.docs.forEach((d) => {
        const data = d.data();
        updates.set(d.id, {
          likes: data.likes || 0,
          commentCount: data.commentCount || 0,
        });
      });
      setPosts((prev) =>
        prev.map((p) => {
          const u = updates.get(p.id);
          if (!u) return p;
          if (u.likes === (p.likes || 0) && u.commentCount === (p.commentCount || 0)) {
            return p; // unchanged → preserve reference so memo'd cards skip render
          }
          return { ...p, likes: u.likes, commentCount: u.commentCount };
        })
      );
    } catch (error) {
      console.error("Error refreshing visible post counts:", error);
    } finally {
      visibleCountsInFlightRef.current = false;
    }
  }, []);

  // Quietly pull newer posts when the user returns to the browser — covers
  // alt-tab back into the window (focus) and switching back to this tab
  // (visibilitychange). refreshIfStale is a no-op when the cache is fresh
  // and bails before any fetch has completed, so listening globally is safe.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }
      void refreshIfStale();
      void refreshVisibleCounts();
    };
    window.addEventListener("focus", handleVisible);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisible);
    }
    return () => {
      window.removeEventListener("focus", handleVisible);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisible);
      }
    };
  }, [refreshIfStale, refreshVisibleCounts]);

  return (
    <PostsContext.Provider
      value={{
        posts,
        setPosts,
        addPost,
        initialLoading,
        backgroundRefreshing,
        loadingMore,
        refreshing,
        hasMore,
        lastFetchedAt,
        fetchInitialPosts,
        fetchMorePosts,
        onRefresh,
        refreshIfStale,
        refreshVisibleCounts,
      }}
    >
      {children}
    </PostsContext.Provider>
  );
};
